import axiosNextGen from '../../../config/axios-nextgen';

/**
 * Patient Tracking API Service
 * Handles patient tracking contact types and actions
 */

// ============================================================================
// Contact Types Endpoints
// ============================================================================

/**
 * Get all contact types ordered by name
 * @returns {Promise<Array<{contactTypeId: string, contactTypeName: string, daysUntilFollowUp: number|null, requiresNonCompliant: boolean}>>}
 */
export async function getAllContactTypes() {
    const response = await axiosNextGen.get('/api/patienttracking/contact-types');
    return response.data;
}

/**
 * Get a specific contact type by ID
 * @param {string} id - The contact type ID (GUID)
 * @returns {Promise<{contactTypeId: string, contactTypeName: string, daysUntilFollowUp: number|null, requiresNonCompliant: boolean}>}
 */
export async function getContactTypeById(id) {
    const response = await axiosNextGen.get(`/api/patienttracking/contact-types/${id}`);
    return response.data;
}

// ============================================================================
// Tracking Actions Endpoints
// ============================================================================

/**
 * Create a new patient tracking action
 * @param {Object} data - The tracking action data
 * @param {string} data.patientResultId - Patient result ID (GUID)
 * @param {string} data.contactTypeId - Contact type ID (GUID)
 * @param {string} [data.contactedByUserId] - User ID who made contact (GUID, optional)
 * @param {string} [data.facilityId] - Facility ID (GUID, optional)
 * @param {string} [data.notes] - Notes (optional)
 * @param {string} [data.nextFollowUpDate] - Next follow-up date (DateOnly format, optional)
 * @param {string} [data.nonCompliantTrackingNumber] - Non-compliant tracking number (optional, max 100 chars)
 * @param {string} [data.nonCompliantAttachment] - Base64 encoded attachment (optional)
 * @param {string} [data.nonCompliantAttachmentFileName] - Attachment filename (optional, max 255 chars)
 * @returns {Promise<Object>} The created tracking action
 */
export async function createTrackingAction(data) {
    const response = await axiosNextGen.post('/api/patienttracking/actions', data);
    return response.data;
}

/**
 * Get all tracking actions for a specific patient result
 * @param {string} patientResultId - The patient result ID (GUID)
 * @returns {Promise<Array<Object>>} Array of tracking actions ordered by created date (newest first)
 */
export async function getActionsByPatientResult(patientResultId) {
    const response = await axiosNextGen.get(`/api/patienttracking/actions/patient-result/${patientResultId}`);
    return response.data;
}

/**
 * Get a specific tracking action by ID
 * @param {string} id - The tracking action ID (GUID)
 * @returns {Promise<Object>} The tracking action
 */
export async function getActionById(id) {
    const response = await axiosNextGen.get(`/api/patienttracking/actions/${id}`);
    return response.data;
}

/**
 * Update an existing tracking action
 * @param {string} id - The tracking action ID (GUID)
 * @param {Object} data - The updated tracking action data
 * @param {string} data.contactTypeId - Contact type ID (GUID)
 * @param {string} [data.contactedByUserId] - User ID who made contact (GUID, optional)
 * @param {string} [data.facilityId] - Facility ID (GUID, optional)
 * @param {string} [data.notes] - Notes (optional)
 * @param {string} [data.nextFollowUpDate] - Next follow-up date (DateOnly format, optional)
 * @param {string} [data.nonCompliantTrackingNumber] - Non-compliant tracking number (optional, max 100 chars)
 * @param {string} [data.nonCompliantAttachment] - Base64 encoded attachment (optional)
 * @param {string} [data.nonCompliantAttachmentFileName] - Attachment filename (optional, max 255 chars)
 * @returns {Promise<Object>} The updated tracking action
 */
export async function updateTrackingAction(id, data) {
    const response = await axiosNextGen.put(`/api/patienttracking/actions/${id}`, data);
    return response.data;
}

/**
 * Delete a tracking action
 * @param {string} id - The tracking action ID (GUID)
 * @returns {Promise<void>}
 */
export async function deleteTrackingAction(id) {
    await axiosNextGen.delete(`/api/patienttracking/actions/${id}`);
}

/**
 * Download the non-compliant letter attachment for a tracking action
 * @param {string} id - The tracking action ID (GUID)
 * @returns {Promise<Blob>} The file blob with response headers containing filename
 */
export async function downloadAttachment(id) {
    const response = await axiosNextGen.get(`/api/patienttracking/actions/${id}/attachment`, {
        responseType: 'blob'
    });
    return response;
}

// ============================================================================
// Default Export
// ============================================================================

const patientTrackingService = {
    getAllContactTypes,
    getContactTypeById,
    createTrackingAction,
    getActionsByPatientResult,
    getActionById,
    updateTrackingAction,
    deleteTrackingAction,
    downloadAttachment
};

export default patientTrackingService;


